import type { AccessLevel } from "@ondo/shared";
import type { ReactNode } from "react";
import { useId, useState } from "react";

import { COLLECTION_COPY } from "./collection.constants.ts";
import { isCollectionNamed } from "./collection.model.ts";
import type { CollectionRow, OwnerChoice } from "./collection.model.ts";

/**
 * Who the new collection belongs to - OND-184, and nothing at all when there is
 * only one answer.
 *
 * `owners` is empty for a reader with no admin seat anywhere, which is every
 * personal collector, and the form then looks exactly as it did. One option is
 * not a choice; a control saying "You" to somebody with no firm would be asking
 * a question with one answer.
 *
 * A function and not two copies, because this screen has TWO create forms - the
 * `?new=1` panel over the grid and the one on the empty branch - and a control
 * added to one of them is the sort of thing that stays missing from the other.
 */
function OwnerField({
  owners,
  value,
  onChange,
}: {
  owners: readonly OwnerChoice[];
  /** The chosen ONDO id, or '' for the caller themselves. */
  value: string;
  onChange: (next: string) => void;
}) {
  const id = useId();
  if (owners.length === 0) return null;

  return (
    <label className="collection__owner" htmlFor={id}>
      <span className="collection__owner-label">{COLLECTION_COPY.ownerLabel}</span>
      <select
        id={id}
        className="collection__owner-select"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {owners.map((owner) => (
          <option key={owner.ondoId ?? "self"} value={owner.ondoId ?? ""}>
            {owner.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The summary line above the grid, and the New collection form beneath it.
 *
 * THE ROW HOLDS NO BUTTONS ANY MORE. It used to carry "New collection" and a
 * second "Register Artwork" under a header that already had the first — two
 * bars for one screen. Both actions live in the shell's header now
 * (`COLLECTOR_PORTAL.primaryAction` and `.secondaryAction`), and since the
 * header is a frame that holds none of this screen's state, "New collection"
 * is a link back to this screen with `FOCUS_PARAMS.create` set. `creating` is
 * that parameter, read by the controller; `onClose` drops it.
 */
export function CollectionActions({
  left,
  creating,
  owners,
  onCreate,
  onClose,
  busy,
}: {
  /** The summary text, when there is one. */
  left?: ReactNode;
  creating: boolean;
  /** OND-184. Who it could belong to; empty draws no picker. */
  owners: readonly OwnerChoice[];
  onCreate: (name: string, ondoId: string | null) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [name, setName] = useState("");
  // '' is the caller themselves, which is the first option and the default -
  // not an inference, because it is the option the reader is looking at.
  const [owner, setOwner] = useState("");
  const id = useId();

  return (
    <>
      {left && <div className="collection__summary">{left}</div>}

      {creating && (
        <form
          className="collection__panel"
          onSubmit={(event) => {
            event.preventDefault();
            onCreate(name, owner === "" ? null : owner);
            // Only a named collection clears the field. An empty one is
            // refused by the controller, and the form stays for the reason
            // `NewCollectionForm` gives: emptying it would hide the field the
            // refusal is about.
            //
            // THE OWNER IS NOT CLEARED, and that is deliberate: a director
            // filing three works into the gallery's stock should not have to
            // re-say whose it is on every one.
            if (isCollectionNamed(name)) setName("");
          }}
        >
          <label htmlFor={id}>{COLLECTION_COPY.newCollection}</label>
          <div className="collection__panel-row">
            <input
              id={id}
              className="collection__input"
              value={name}
              autoFocus
              placeholder="Post-War British"
              onChange={(event) => setName(event.target.value)}
            />
            <OwnerField owners={owners} value={owner} onChange={setOwner} />
            <button type="submit" className="collection__submit" disabled={busy}>
              Create
            </button>
            <button type="button" className="collection__cancel" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </>
  );
}

export function NewCollectionForm({
  owners,
  onSubmit,
  busy,
}: {
  /** OND-184. Who it could belong to; empty draws no picker. */
  owners: readonly OwnerChoice[];
  onSubmit: (name: string, ondoId: string | null) => void;
  busy: boolean;
}) {
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const id = useId();

  return (
    <form
      className="collection__form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(name, owner === "" ? null : owner);
        // Cleared only when there was something to send, which is the rule
        // `CollectionActions` states above and this form disagreed with: a
        // blank name is refused by the controller, and emptying the field on
        // the way out takes away the field the refusal is about.
        if (isCollectionNamed(name)) setName("");
      }}
    >
      <label htmlFor={id}>{COLLECTION_COPY.newCollection}</label>
      <input
        id={id}
        className="collection__input"
        value={name}
        placeholder="Post-War British"
        onChange={(event) => setName(event.target.value)}
      />
      <OwnerField owners={owners} value={owner} onChange={setOwner} />
      <button type="submit" className="collection__submit" disabled={busy}>
        Create
      </button>
    </form>
  );
}

/**
 * Sharing a collection by ONDO id.
 *
 * Only collections the caller owns appear here: `canShare` comes from the
 * server's effective level, and sharing is an owner's right so that someone
 * invited to help catalogue cannot widen the audience they were let into.
 */
export function ShareForm({
  collections,
  onSubmit,
  busy,
}: {
  collections: CollectionRow[];
  onSubmit: (input: { collectionId: string; ondoId: string; level: AccessLevel }) => void;
  busy: boolean;
}) {
  const shareable = collections.filter((collection) => collection.canShare);
  const [chosen, setChosen] = useState<string | null>(null);
  const [ondoId, setOndoId] = useState("");
  const [level, setLevel] = useState<AccessLevel>("viewer");
  const collectionField = useId();
  const idField = useId();
  const levelField = useId();

  if (shareable.length === 0) return null;

  /**
   * DERIVED, not seeded once into state.
   *
   * `useState(shareable[0]?.id)` ran on the first render and never again, and
   * this component is not remounted when the collection list changes — so a
   * collection that stopped being shareable left a stale but TRUTHY id behind.
   * The `<select>` then matched no option while the submit still carried the
   * old id, and the fallback below never fired, because it only caught the
   * empty string. Falling back on "no longer in the list" is what makes the
   * control and the request agree.
   */
  const selected = shareable.some((collection) => collection.id === chosen)
    ? (chosen ?? "")
    : (shareable[0]?.id ?? "");

  return (
    <form
      className="collection__form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ collectionId: selected, ondoId, level });
        setOndoId("");
      }}
    >
      <label htmlFor={collectionField}>Share</label>
      <select
        id={collectionField}
        value={selected}
        onChange={(event) => setChosen(event.target.value)}
      >
        {shareable.map((collection) => (
          <option key={collection.id} value={collection.id}>
            {collection.name}
          </option>
        ))}
      </select>

      <label htmlFor={idField}>with</label>
      <input
        id={idField}
        className="collection__input"
        value={ondoId}
        placeholder="ONDO-1042"
        onChange={(event) => setOndoId(event.target.value)}
      />

      <label htmlFor={levelField}>as</label>
      <select
        id={levelField}
        value={level}
        onChange={(event) => setLevel(event.target.value as AccessLevel)}
      >
        <option value="viewer">Viewer</option>
        <option value="curator">Curator</option>
      </select>

      <button type="submit" className="collection__submit" disabled={busy}>
        Share
      </button>
    </form>
  );
}
